import type { FC, PropsWithChildren } from "react";
import { createContext, useContext, useMemo, useState } from "react";

import { isClient } from "@/lib/common";
import { deleteCookie, getCookie } from "@Components/Modules/Helpers";

export interface ILocation {
    placeId: string;
    placeDescription: string;
    latitude: number;
    longitude: number;
}

const defaultLocation: ILocation = {
    placeId: "ChIJ8ZMYu6jNHkcRKqCBUGorYvM",
    placeDescription: "Warszawa",
    latitude: 52.2296756,
    longitude: 21.0122287,
};

interface ILocationContext {
    location: ILocation;
    setLocation: (location: ILocation) => void;
    refreshLocation: () => void;
}

export const LocationContext = createContext<ILocationContext>(null);

export const useLocationContext = () => useContext(LocationContext);

const locationCookieName = "location";

export const LocationContextProvider: FC<PropsWithChildren<{ userLocation: ILocation }>> = ({
    children,
    userLocation,
}) => {
    const [location, setLocation] = useState(userLocation || defaultLocation);
    const refreshLocation = () => {
        if (!isClient) return;
        const locationList = atob(getCookie(locationCookieName) || "").split(";");
        if (locationList.length === 4) {
            if (location.placeId !== locationList[0]) {
                setLocation({
                    placeId: locationList[0],
                    placeDescription: decodeURIComponent(locationList[1]).replaceAll("+", " "),
                    latitude: Number.parseFloat(locationList[2]),
                    longitude: Number.parseFloat(locationList[3]),
                });
            }
        } else {
            deleteCookie(locationCookieName);
        }
    };

    const context = useMemo(() => ({ location, setLocation, refreshLocation }), [location, setLocation]);

    return <LocationContext.Provider value={context}>{children}</LocationContext.Provider>;
};
